import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Iterable } from 'vscode/vscode/vs/base/common/iterator';
import { DisposableStore, toDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { isAccessibilityInformation } from 'vscode/vscode/vs/platform/accessibility/common/accessibility';
import { ExtensionIdentifier } from 'vscode/vscode/vs/platform/extensions/common/extensions';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { asStatusBarItemIdentifier } from 'vscode/vscode/vs/workbench/api/common/extHostTypes';
import { registerWorkbenchContribution2 } from 'vscode/vscode/vs/workbench/common/contributions';
import { isProposedApiEnabled } from 'vscode/vscode/vs/workbench/services/extensions/common/extensions';
import { ExtensionsRegistry } from 'vscode/vscode/vs/workbench/services/extensions/common/extensionsRegistry';
import { IExtensionStatusBarItemService } from 'vscode/vscode/vs/workbench/api/browser/statusBarService';

const _moduleId = "vs/workbench/api/browser/statusBarExtensionPoint";
function isUserFriendlyStatusItemEntry(candidate) {
    const obj = candidate;
    return (typeof obj.id === 'string' && obj.id.length > 0)
        && typeof obj.name === 'string'
        && typeof obj.text === 'string'
        && (obj.alignment === 'left' || obj.alignment === 'right')
        && (obj.command === undefined || typeof obj.command === 'string')
        && (obj.tooltip === undefined || typeof obj.tooltip === 'string')
        && (obj.priority === undefined || typeof obj.priority === 'number')
        && (obj.accessibilityInformation === undefined || isAccessibilityInformation(obj.accessibilityInformation));
}
const statusBarItemSchema = {
    type: 'object',
    required: ['id', 'text', 'alignment', 'name'],
    properties: {
        id: {
            type: 'string',
            markdownDescription: ( localizeWithPath(
                _moduleId,
                0,
                'The identifier of the status bar entry. Must be unique within the extension. The same value must be used when calling the `vscode.window.createStatusBarItem(id, ...)`-API'
            ))
        },
        name: {
            type: 'string',
            description: ( localizeWithPath(
                _moduleId,
                1,
                'The name of the entry, like \'Python Language Indicator\', \'Git Status\' etc. Try to keep the length of the name short, yet descriptive enough that users can understand what the status bar item is about.'
            ))
        },
        text: {
            type: 'string',
            description: ( localizeWithPath(
                _moduleId,
                2,
                'The text to show for the entry. You can embed icons in the text by leveraging the `$(<name>)`-syntax, like \'Hello $(globe)!\''
            ))
        },
        tooltip: {
            type: 'string',
            description: ( localizeWithPath(_moduleId, 3, 'The tooltip text for the entry.'))
        },
        command: {
            type: 'string',
            description: ( localizeWithPath(
                _moduleId,
                4,
                'The command to execute when the status bar entry is clicked.'
            ))
        },
        alignment: {
            type: 'string',
            enum: ['left', 'right'],
            description: ( localizeWithPath(_moduleId, 5, 'The alignment of the status bar entry.'))
        },
        priority: {
            type: 'number',
            description: ( localizeWithPath(
                _moduleId,
                6,
                'The priority of the status bar entry. Higher value means the item should be shown more to the left.'
            ))
        },
        accessibilityInformation: {
            type: 'object',
            description: ( localizeWithPath(
                _moduleId,
                7,
                'Defines the role and aria label to be used when the status bar entry is focused.'
            )),
            properties: {
                role: {
                    type: 'string',
                    description: ( localizeWithPath(
                        _moduleId,
                        8,
                        'The role of the status bar entry which defines how a screen reader interacts with it. More about aria roles can be found here https://w3c.github.io/aria/#widget_roles'
                    ))
                },
                label: {
                    type: 'string',
                    description: ( localizeWithPath(
                        _moduleId,
                        9,
                        'The aria label of the status bar entry. Defaults to the entry\'s text.'
                    ))
                }
            }
        }
    }
};
const statusBarItemsSchema = {
    description: ( localizeWithPath(_moduleId, 10, "Contributes items to the status bar.")),
    oneOf: [
        statusBarItemSchema,
        {
            type: 'array',
            items: statusBarItemSchema
        }
    ]
};
const statusBarItemsExtensionPoint = ExtensionsRegistry.registerExtensionPoint({
    extensionPoint: 'statusBarItems',
    jsonSchema: statusBarItemsSchema,
});
let StatusBarItemsExtensionPoint = class StatusBarItemsExtensionPoint {
    constructor(statusBarItemsService) {
        const contributions = ( (new DisposableStore()));
        statusBarItemsExtensionPoint.setHandler((extensions) => {
            contributions.clear();
            for (const entry of extensions) {
                if (!isProposedApiEnabled(entry.description, 'contribStatusBarItems')) {
                    entry.collector.error(`The ${statusBarItemsExtensionPoint.name} is proposed API`);
                    continue;
                }
                const { value, collector } = entry;
                for (const candidate of Iterable.wrap(value)) {
                    if (!isUserFriendlyStatusItemEntry(candidate)) {
                        collector.error(( localizeWithPath(_moduleId, 11, "Invalid status bar item contribution.")));
                        continue;
                    }
                    const fullItemId = asStatusBarItemIdentifier(entry.description.identifier, candidate.id);
                    const kind = statusBarItemsService.setOrUpdateEntry(fullItemId, fullItemId, ExtensionIdentifier.toKey(entry.description.identifier), candidate.name ?? entry.description.displayName ?? entry.description.name, candidate.text, candidate.tooltip, candidate.command ? { id: candidate.command, title: candidate.name } : undefined, undefined, undefined, candidate.alignment === 'left', candidate.priority, candidate.accessibilityInformation);
                    if (kind === 0 ) {
                        contributions.add(toDisposable(() => statusBarItemsService.unsetEntry(fullItemId)));
                    }
                }
            }
        });
    }
};
StatusBarItemsExtensionPoint = ( (__decorate([
    ( (__param(0, IExtensionStatusBarItemService)))
], StatusBarItemsExtensionPoint)));
let StatusBarItemsExtensionPointWorkbenchContribution = class StatusBarItemsExtensionPointWorkbenchContribution {
    static { this.ID = 'workbench.contrib.statusBarItemsExtensionPoint'; }
    constructor(instantiationService) {
        this.instantiationService = instantiationService;
        this.instantiationService.createInstance(StatusBarItemsExtensionPoint);
    }
};
StatusBarItemsExtensionPointWorkbenchContribution = ( (__decorate([
    ( (__param(0, IInstantiationService)))
], StatusBarItemsExtensionPointWorkbenchContribution)));
registerWorkbenchContribution2(StatusBarItemsExtensionPointWorkbenchContribution.ID, StatusBarItemsExtensionPointWorkbenchContribution, 1 );

export { StatusBarItemsExtensionPoint };
